import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActivityLoader from "../../components/Common/ActivityLoader";
import Notification from "../../components/Common/Notification";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import BsModal from "../../components/Common/BsModal";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../DataStore/ConfigContext";
import ajaxStudent from "../../util/remote/ajaxStudent";

/**
 * StudentTransferPage
 * - NO static/dummy class/stream data (uses server classes from ConfigContext)
 * - Transfer entire class/stream OR mark class as Former
 * - Destination must be empty: UI enforces via `destinationCount` (wire to server later)
 *
 * NOTE:
 * - This page assumes you will provide an API method for destination occupancy, e.g.
 *   getClassOccupancy({ class_id, stream_id }) -> { count: number }
 * - For now, we display the "Check occupancy" button + placeholder count state,
 *   but we do NOT use any hard-coded/dummy occupancy map.
 */
const StudentTransferPage = () => {
  const navigate = useNavigate();
  const { getClasses, classes /*, getClassOccupancy */ } = useConfig();

  useEffect(() => {
    getClasses();
    // eslint-disable-next-line
  }, []);

  // -----------------------------
  // Normalize classes from server
  // -----------------------------
  const classCatalog = useMemo(() => {
    const list = Array.isArray(classes) ? classes : [];

    const getOrder = (name = "") => {
      const m = String(name).match(/(\d+)/);
      return m ? Number(m[1]) : 999;
    };

    return list
      .map((c) => {
        const streamsArr = Array.isArray(c.streams) ? c.streams : [];
        return {
          class_id: String(c.class_id),
          class_name: String(c.class_name || ""),
          level_id: String(c?.level?.level_id || ""),
          level_name: String(c?.level?.level || ""),
          streamed: streamsArr.length > 0,
          streams: streamsArr.map((s) => ({
            stream_id: String(s.stream_id),
            stream_name: String(s.stream_name || ""),
          })),
        };
      })
      .sort((a, b) => getOrder(a.class_name) - getOrder(b.class_name));
  }, [classes]);

  const classesLoading = !Array.isArray(classes);

  // Final year rule (adjust if needed)
  const isFinalYearClass = (cls) => String(cls?.class_name || "").trim() === "S.6";

  // -----------------------------
  // UI State
  // -----------------------------
  const [tab, setTab] = useState("transfer"); // "transfer" | "former"
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Transfer selections
  const [fromClassId, setFromClassId] = useState("");
  const [fromStreamId, setFromStreamId] = useState("");

  const [toClassId, setToClassId] = useState("");
  const [toStreamId, setToStreamId] = useState("");

  // Former selections
  const [formerClassId, setFormerClassId] = useState("");
  const [formerStreamId, setFormerStreamId] = useState("");
  const [formerDate, setFormerDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [formerReason, setFormerReason] = useState("Completed final year");

  // Destination occupancy state (NO dummy data)
  const [destCount, setDestCount] = useState(null); // null = unknown
  const [checkingDest, setCheckingDest] = useState(false);

  // -----------------------------
  // Default selections (after classes load)
  // -----------------------------
  useEffect(() => {
    if (!classCatalog.length) return;

    if (!fromClassId) {
      const s4 = classCatalog.find((c) => c.class_name === "S.4") || classCatalog[0];
      setFromClassId(s4?.class_id || "");
      setFromStreamId(s4?.streamed ? s4.streams[0]?.stream_id || "" : "");
    }

    if (!toClassId) {
      const s5 = classCatalog.find((c) => c.class_name === "S.5") || classCatalog[Math.min(1, classCatalog.length - 1)];
      setToClassId(s5?.class_id || "");
      setToStreamId(s5?.streamed ? s5.streams[0]?.stream_id || "" : "");
    }

    if (!formerClassId) {
      const s6 = classCatalog.find((c) => c.class_name === "S.6") || classCatalog[classCatalog.length - 1];
      setFormerClassId(s6?.class_id || "");
      setFormerStreamId(s6?.streamed ? s6.streams[0]?.stream_id || "" : "");
    }
    // eslint-disable-next-line
  }, [classCatalog]);

  // -----------------------------
  // Derived selections
  // -----------------------------
  const fromClass = useMemo(() => classCatalog.find((c) => c.class_id === String(fromClassId)), [classCatalog, fromClassId]);
  const toClass = useMemo(() => classCatalog.find((c) => c.class_id === String(toClassId)), [classCatalog, toClassId]);
  const formerClass = useMemo(() => classCatalog.find((c) => c.class_id === String(formerClassId)), [classCatalog, formerClassId]);

  const fromIsStreamed = !!fromClass?.streamed;
  const toIsStreamed = !!toClass?.streamed;
  const formerIsStreamed = !!formerClass?.streamed;

  // keep stream selections valid
  useEffect(() => {
    if (!fromClass) return;
    if (!fromClass.streamed) return setFromStreamId("");
    const exists = fromClass.streams.some((s) => s.stream_id === String(fromStreamId));
    if (!exists) setFromStreamId(fromClass.streams[0]?.stream_id || "");
    // eslint-disable-next-line
  }, [fromClassId]);

  useEffect(() => {
    if (!toClass) return;
    if (!toClass.streamed) return setToStreamId("");
    const exists = toClass.streams.some((s) => s.stream_id === String(toStreamId));
    if (!exists) setToStreamId(toClass.streams[0]?.stream_id || "");
    // eslint-disable-next-line
  }, [toClassId]);

  useEffect(() => {
    if (!formerClass) return;
    if (!formerClass.streamed) return setFormerStreamId("");
    const exists = formerClass.streams.some((s) => s.stream_id === String(formerStreamId));
    if (!exists) setFormerStreamId(formerClass.streams[0]?.stream_id || "");
    // eslint-disable-next-line
  }, [formerClassId]);

  // reset destination count whenever destination changes
  useEffect(() => {
    setDestCount(null);
  }, [toClassId, toStreamId]);

  const sameDestination = useMemo(() => {
    if (!fromClassId || !toClassId) return false;
    if (String(fromClassId) !== String(toClassId)) return false;

    if (!fromIsStreamed && !toIsStreamed) return true;
    if (fromIsStreamed && toIsStreamed && String(fromStreamId) === String(toStreamId)) return true;

    return false;
  }, [fromClassId, toClassId, fromIsStreamed, toIsStreamed, fromStreamId, toStreamId]);

  const destinationIsEmpty = destCount === 0;

  const canProceedTransfer = useMemo(() => {
    if (loading || classesLoading) return false;
    if (!fromClassId || !toClassId) return false;
    if (sameDestination) return false;

    if (fromIsStreamed && !fromStreamId) return false;
    if (toIsStreamed && !toStreamId) return false;

    // must have checked destination and it must be empty
    if (destCount === null) return false;
    // if (!destinationIsEmpty) return false;

    return true;
  }, [
    loading,
    classesLoading,
    fromClassId,
    toClassId,
    sameDestination,
    fromIsStreamed,
    fromStreamId,
    toIsStreamed,
    toStreamId,
    destCount,
    // destinationIsEmpty,
  ]);

  const formerIsFinalYear = useMemo(() => isFinalYearClass(formerClass), [formerClass]);

  const canProceedFormer = useMemo(() => {
    if (loading || classesLoading) return false;
    if (!formerClassId) return false;
    if (!formerIsFinalYear) return false;
    if (formerIsStreamed && !formerStreamId) return false;
    if (!formerDate) return false;
    return true;
  }, [loading, classesLoading, formerClassId, formerIsFinalYear, formerIsStreamed, formerStreamId, formerDate]);

  const fromStream = useMemo(() => {
  if (!fromIsStreamed) return null;
  return (fromClass?.streams || []).find((s) => String(s.stream_id) === String(fromStreamId)) || null;
}, [fromIsStreamed, fromClass, fromStreamId]);

const toStream = useMemo(() => {
if (!toIsStreamed) return null;
return (toClass?.streams || []).find((s) => String(s.stream_id) === String(toStreamId)) || null;
}, [toIsStreamed, toClass, toStreamId]);


  // -----------------------------
  // Actions
  // -----------------------------
  const resetNotices = () => setNotify(null);

  const handleCheckDestination = async () => {
    resetNotices();

    if (!toClassId) {
      setNotify(<Notification type="danger" message="Please select a destination class first." />);
      return;
    }
    if (toIsStreamed && !toStreamId) {
      setNotify(<Notification type="danger" message="Please select a destination stream." />);
      return;
    }

    setCheckingDest(true);
    const serverResponse = await ajaxStudent.fetchClassPopulation({ class_id: toClassId, stream_id: toIsStreamed ? toStreamId : null });
    setCheckingDest(false);
    if(serverResponse.status === "OK"){
        setDestCount(Number(serverResponse.details?.total) || 0);
    }else{
        setNotify(<Notification type="danger" message="Failed to fetch destination occupancy. Please try again." />);
    }

  };

  const handleOpenConfirm = () => {
    resetNotices();

    if (destCount === null) {
      setNotify(<Notification type="warning" message='Please click "Check destination" to confirm the class is empty.' />);
      return;
    }

    if (!canProceedTransfer) {
      if (!destinationIsEmpty) {
        setNotify(
          <Notification
            type="danger"
            message={`Destination is not empty (${destCount} student(s)). Please clear it before transferring a full class.`}
          />
        );
      } else if (sameDestination) {
        setNotify(<Notification type="danger" message="Source and destination are the same. Please choose a different destination." />);
      } else {
        setNotify(<Notification type="danger" message="Please complete all selections first." />);
      }
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmTransfer = async () => {
    setConfirmOpen(false);
    setLoading(true);
    const serverResponse = await ajaxStudent.transferStudents({
      from_class_id: fromClassId,
      from_stream_id: fromIsStreamed ? fromStreamId : '0',
      to_class_id: toClassId,
      to_stream_id: toIsStreamed ? toStreamId : '0',
    });
    setLoading(false);
    if (serverResponse.status === "OK") {
        setNotify(
        <Notification
            type="success"
            message={`Transferred ${fromClass?.class_name || "—"} → ${toClass?.class_name || "—"} successfully.`}
        />
        );
    } else {
        setNotify(<Notification type="danger" message={serverResponse.message || "Failed to transfer students. Please try again."} />);
    }

    // destination changed, require re-check next time
    setDestCount(null);
  };

  const handleMarkFormer = async () => {
    resetNotices();

    if (!canProceedFormer) {
      setNotify(
        <Notification
          type="warning"
          message="Proceeding with this action, ignoring warnings..."
        />
      );
    }

    setLoading(true);
    const serverResponse = await ajaxStudent.markClassCompleted({
        class_id: formerClassId,
        stream_id: formerIsStreamed ? formerStreamId : "0",
        date_completed: formerDate,
        reason: formerReason,
    });
    setLoading(false);
    if(serverResponse.status === "OK"){
        setNotify(<Notification type="success" message={`Marked ${formerClass?.class_name || "—"} as Former successfully.`} />);
    } else {
        setNotify(<Notification type="danger" message={serverResponse.message || "Failed to mark class as Former. Please try again."} />);
    }
  };

  // -----------------------------
  // UI helpers
  // -----------------------------
  const SegButton = ({ active, icon, title, desc, onClick }) => (
    <button
      type="button"
      className={`btn w-100 text-left ${active ? "btn-primary" : "btn-outline-primary"}`}
      onClick={onClick}
      style={{ borderRadius: 12, padding: 14 }}
    >
      <div className="d-flex align-items-start">
        <div className="mr-3" style={{ width: 32 }}>
          <i className={`fa ${icon}`} style={{ fontSize: 18 }} />
        </div>
        <div className="flex-grow-1">
          <div className="font-weight-bold">{title}</div>
          <div className="tx-12" style={{ opacity: active ? 0.85 : 0.75 }}>
            {desc}
          </div>
        </div>
      </div>
    </button>
  );

  const StreamChips = ({ options = [], value, onChange, variant = "primary" }) => (
    <div className="d-flex flex-wrap" style={{ gap: 10 }}>
      {options.map((s) => (
        <button
          key={s.stream_id}
          type="button"
          className={`btn btn-sm ${String(value) === String(s.stream_id) ? `btn-${variant}` : `btn-outline-${variant}`}`}
          onClick={() => onChange(String(s.stream_id))}
          style={{ borderRadius: 999 }}
        >
          <i className="fa fa-layer-group mr-1" />
          {s.stream_name}
        </button>
      ))}
    </div>
  );

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Student Transfer"
          description="Move an entire class/stream to a new class, or mark finalists as former."
          iconClass="fa fa-exchange-alt"
          sticky={true}
        />

        {/* Top action selector (mobile friendly) */}
        <div className="row mb-3">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-lg-0">
                    <div className="font-weight-bold">Choose an action</div>
                    <div className="text-muted tx-13">
                      Move a <b>full class</b> (or stream), or mark a <b>final year class</b> as Former.
                    </div>
                    {classesLoading && (
                      <div className="mt-2">
                        <ActivityLoader />
                      </div>
                    )}
                  </div>

                  <div className="col-lg-6">
                    <div className="row">
                      <div className="col-12 col-md-6 mb-2 mb-md-0">
                        <SegButton
                          active={tab === "transfer"}
                          icon="fa-random"
                          title="Transfer / Promote Class"
                          desc='Requires "Check destination" to confirm it’s empty.'
                          onClick={() => (setTab("transfer"), resetNotices())}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <SegButton
                          active={tab === "former"}
                          icon="fa-user-graduate"
                          title="Mark Class as Former"
                          desc="Only allowed for final year classes (e.g. S.6)."
                          onClick={() => (setTab("former"), resetNotices())}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3">{notify}</div>
              </div>
            </div>
          </div>
        </div>

        {/* TRANSFER TAB */}
        {tab === "transfer" && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card mb-3">
                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                  <div className="font-weight-bold">
                    <i className="fa fa-random text-primary mr-2" />
                    Transfer Setup
                  </div>
                  <span className="badge badge-light">
                    <i className="fa fa-shield-alt mr-1" />
                    Destination must be empty
                  </span>
                </div>

                <div className="card-body">
                  {classesLoading ? (
                    <ActivityLoader />
                  ) : (
                    <>
                      <div className="row">
                        {/* Source */}
                        <div className="col-md-6">
                          <div className="p-3 rounded" style={{ border: "1px solid #e9ecef", background: "#fbfdff" }}>
                            <div className="font-weight-bold mb-2">
                              <i className="fa fa-sign-out-alt text-secondary mr-2" />
                              From
                            </div>

                            <div className="form-group">
                              <label className="tx-12 text-muted mb-1">Class</label>
                              <select className="form-control" value={fromClassId} onChange={(e) => setFromClassId(e.target.value)}>
                                {classCatalog.map((c) => (
                                  <option key={c.class_id} value={c.class_id}>
                                    {c.class_name} {c.level_name ? `— ${c.level_name}` : ""}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {fromIsStreamed && (
                              <div className="form-group mb-0">
                                <label className="tx-12 text-muted mb-2">Stream</label>
                                <StreamChips options={fromClass.streams} value={fromStreamId} onChange={setFromStreamId} variant="primary" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Destination */}
                        <div className="col-md-6">
                          <div className="p-3 rounded" style={{ border: "1px solid #e9ecef", background: "#fbfffb" }}>
                            <div className="font-weight-bold mb-2">
                              <i className="fa fa-sign-in-alt text-success mr-2" />
                              To
                            </div>

                            <div className="form-group">
                              <label className="tx-12 text-muted mb-1">Class</label>
                              <select className="form-control" value={toClassId} onChange={(e) => setToClassId(e.target.value)}>
                                {classCatalog.map((c) => (
                                  <option key={c.class_id} value={c.class_id}>
                                    {c.class_name} {c.level_name ? `— ${c.level_name}` : ""}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {toIsStreamed && (
                              <div className="form-group mb-0">
                                <label className="tx-12 text-muted mb-2">Stream</label>
                                <StreamChips options={toClass.streams} value={toStreamId} onChange={setToStreamId} variant="success" />
                              </div>
                            )}

                            <div className="mt-3 d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 10 }}>
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={handleCheckDestination}
                                disabled={checkingDest || !toClassId || (toIsStreamed && !toStreamId)}
                              >
                                {checkingDest ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin mr-2" />
                                    Checking...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-search mr-2" />
                                    Check destination
                                  </>
                                )}
                              </button>

                              <span className={`badge ${destCount === null ? "badge-light" : destinationIsEmpty ? "badge-success" : "badge-warning"}`}>
                                {destCount === null ? "Not checked" : `${destCount} student(s)`}
                              </span>
                            </div>

                            {destCount !== null && !destinationIsEmpty && (
                              <div className="tx-12 text-danger mt-2">
                                <i className="fa fa-exclamation-triangle mr-1" />
                                Clear destination before transferring.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {sameDestination && (
                        <div className="alert alert-warning mt-3 mb-0">
                          <i className="fa fa-exclamation-triangle mr-2" />
                          Source and destination are the same. Please select a different destination.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Review & Transfer */}
            <div className="col-lg-4">
              <div className="card mb-3">
                <div className="card-header bg-white">
                  <div className="font-weight-bold">
                    <i className="fa fa-clipboard-check text-primary mr-2" />
                    Review & Transfer
                  </div>
                </div>

                <div className="card-body">
                  <div className="p-3 rounded mb-3" style={{ background: "#f7fbff", border: "1px solid #dbeafe" }}>
                    <div className="tx-12 text-muted mb-1">You are moving:</div>

                    <div className="font-weight-bold">
                    {fromClass?.class_name || "—"}
                    {fromIsStreamed ? ` (${fromStream?.stream_name || "—"})` : ""} →{" "}
                    {toClass?.class_name || "—"}
                    {toIsStreamed ? ` (${toStream?.stream_name || "—"})` : ""}
                    </div>


                    <div className="mt-2 tx-12 text-muted">
                      <i className={`fa ${destCount === null ? "fa-question-circle text-muted" : destinationIsEmpty ? "fa-check-circle text-success" : "fa-times-circle text-danger"} mr-1`} />
                      {destCount === null ? "Destination not checked" : destinationIsEmpty ? "Destination is empty" : "Destination is not empty"}
                    </div>
                  </div>

                  <button className="btn btn-success btn-block" disabled={!canProceedTransfer} onClick={handleOpenConfirm}>
                    <i className="fa fa-arrow-right mr-2" />
                    Transfer Entire Class
                  </button>

                  {loading && (
                    <div className="mt-3">
                      <ActivityLoader />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FORMER TAB */}
        {tab === "former" && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card mb-3">
                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                  <div className="font-weight-bold">
                    <i className="fa fa-user-graduate text-info mr-2" />
                    Mark Class as Former
                  </div>
                  <span className={`badge ${formerIsFinalYear ? "badge-info" : "badge-warning"}`}>
                    {formerIsFinalYear ? "Final year class" : "Not final year"}
                  </span>
                </div>

                <div className="card-body">
                  {classesLoading ? (
                    <ActivityLoader />
                  ) : (
                    <>
                      <div className="alert alert-light">
                        <i className="fa fa-info-circle mr-2" />
                        Select a <b>final year class</b> (e.g. S.6) and optionally a stream, then mark as Former.
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="tx-12 text-muted mb-1">Class to mark as Former</label>
                            <select className="form-control" value={formerClassId} onChange={(e) => setFormerClassId(e.target.value)}>
                              {classCatalog.map((c) => (
                                <option key={c.class_id} value={c.class_id}>
                                  {c.class_name} {c.level_name ? `— ${c.level_name}` : ""}
                                </option>
                              ))}
                            </select>

                            {!formerIsFinalYear && (
                              <div className="tx-12 text-danger mt-1">
                                <i className="fa fa-exclamation-triangle mr-1" />
                                Only final year classes can be marked as Former.
                              </div>
                            )}
                          </div>

                          {formerIsStreamed && (
                            <div className="form-group">
                              <label className="tx-12 text-muted mb-2">Stream</label>
                              <StreamChips options={formerClass.streams} value={formerStreamId} onChange={setFormerStreamId} variant="info" />
                            </div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="tx-12 text-muted mb-1">Former Date</label>
                            <input className="form-control" type="date" value={formerDate} onChange={(e) => setFormerDate(e.target.value)} />
                          </div>

                          <div className="form-group">
                            <label className="tx-12 text-muted mb-1">Reason</label>
                            <input
                              className="form-control"
                              placeholder="e.g. Completed final year"
                              value={formerReason}
                              onChange={(e) => setFormerReason(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="p-3 rounded" style={{ background: "#f7fbff", border: "1px solid #dbeafe" }}>
                    <div className="tx-12 text-muted mb-1">Preview</div>
                    <div className="font-weight-bold">
                      Mark {formerClass?.class_name || "—"} as Former on {formerDate}
                    </div>
                    <div className="tx-12 text-muted mt-1">
                      <i className="fa fa-note-sticky mr-1" />
                      {formerReason || "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Former action card */}
            <div className="col-lg-4">
              <div className="card mb-3">
                <div className="card-header bg-white">
                  <div className="font-weight-bold">
                    <i className="fa fa-check-circle text-info mr-2" />
                    Confirm Former
                  </div>
                </div>

                <div className="card-body">
                  <button className="btn btn-info btn-block"  onClick={handleMarkFormer}>
                    <i className="fa fa-user-check mr-2" />
                    Mark Class as Former
                  </button>

                  {loading && (
                    <div className="mt-3">
                      <ActivityLoader />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Transfer Modal */}
        <BsModal
          show={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title="Confirm Class Transfer"
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" onClick={handleConfirmTransfer}>
                <i className="fa fa-check mr-2" />
                Yes, Transfer
              </button>
            </>
          }
        >
          <div className="alert alert-warning">
            <i className="fa fa-exclamation-triangle mr-2" />
            This will move the <b>entire class</b> (or stream) to the destination.
          </div>

          <div className="p-3 rounded" style={{ background: "#f7fbff", border: "1px solid #dbeafe" }}>
            <div className="tx-12 text-muted mb-1">Transfer summary</div>
            <div className="font-weight-bold">
            {fromClass?.class_name || "—"}
            {fromIsStreamed ? ` (${fromStream?.stream_name || "—"})` : ""} →{" "}
            {toClass?.class_name || "—"}
            {toIsStreamed ? ` (${toStream?.stream_name || "—"})` : ""}
            </div>
            <div className="tx-12 text-muted mt-2">
              <i className="fa fa-shield-alt mr-1" />
              Destination check: {destCount === null ? "Not checked" : `${destCount} student(s)`}
            </div>
          </div>
        </BsModal>
      </MainContent>
    </PageContainer>
  );
};

export default StudentTransferPage;
